import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Lock, Bell, BellOff, LogOut, User, Loader2, Zap, Star } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import ProfilePhotoSection from '../components/ProfilePhotoSection';
import { enablePush, disablePush, getPushState, isPushSupported } from '../lib/push';
import { Link } from 'react-router-dom';

export default function Impostazioni() {
  const { user, logout, refresh } = useAuth();
  const [showPw, setShowPw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, permission: 'default', subscribed: false, enabled: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  const refreshPush = async () => {
    const s = await getPushState();
    setPushState(s);
  };

  useEffect(() => { refreshPush(); }, []);

  const changePw = async () => {
    if (newPw.length < 3) return toast.error('Password troppo corta');
    setSaving(true);
    try {
      await api.put('/users/me/password', { password: newPw });
      toast.success('Password aggiornata');
      setNewPw('');
      setShowPw(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const togglePush = async () => {
    if (!isPushSupported()) {
      toast.error('Notifiche non supportate su questo browser. Su iPhone installa l\'app dalla schermata Home.');
      return;
    }
    setPushBusy(true);
    try {
      const currentlyActive = pushState.subscribed && pushState.enabled;
      if (currentlyActive) {
        await disablePush();
        toast.success('Notifiche disattivate');
      } else {
        const res = await enablePush();
        if (res.ok) {
          await api.put('/push/settings', { enabled: true });
          toast.success('Notifiche attivate');
        } else if (res.reason === 'denied') {
          toast.error('Permesso negato. Consenti le notifiche dalle impostazioni del browser.');
        } else {
          toast.error('Impossibile attivare le notifiche');
        }
      }
      await refreshPush();
    } catch (e) {
      toast.error('Errore durante l\'operazione');
    } finally {
      setPushBusy(false);
    }
  };

  const pushActive = pushState.subscribed && pushState.enabled;
  const pushBlocked = pushState.permission === 'denied';

  const sendTestPush = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post('/push/test');
      if (!data.subscriptions) {
        toast.error('Nessun dispositivo registrato. Attiva prima le notifiche su questo dispositivo.');
      } else if (data.sent === 0) {
        toast.error(`Invio fallito su ${data.subscriptions} dispositivi (potrebbero essere scaduti)`);
      } else {
        toast.success(`Notifica di prova inviata a ${data.sent} dispositivo${data.sent > 1 ? 'i' : ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio test');
    } finally {
      setTestBusy(false);
    }
  };

  return (
    <Layout title="Impostazioni" showBack backTo="/home">
      <div className="px-5 pt-6 max-w-md mx-auto space-y-4">
        <div className="card-surface p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[var(--gold-soft)] border border-[var(--gold)]/30 flex items-center justify-center">
            <User className="w-6 h-6 text-[var(--gold)]" />
          </div>
          <div>
            <div className="font-heading text-lg font-medium">{user?.username}</div>
            <div className="text-xs text-white/50">Account attivo</div>
          </div>
        </div>

        <ProfilePhotoSection user={user} onUpdate={refresh} />

        <Link
          data-testid="settings-preferiti"
          to="/preferiti"
          className="card-surface p-4 w-full flex items-center gap-4 text-left"
        >
          <Star className="w-5 h-5 text-[var(--gold)] fill-[var(--gold)]" />
          <div className="flex-1">
            <div className="font-heading text-sm font-medium">Messaggi preferiti</div>
            <div className="text-xs text-white/50">Ritrova rapidamente ciò che hai contrassegnato con ★</div>
          </div>
        </Link>

        <button
          data-testid="toggle-password-change-btn"
          onClick={() => setShowPw(!showPw)}
          className="card-surface p-4 w-full flex items-center gap-4 text-left"
        >
          <Lock className="w-5 h-5 text-[var(--gold)]" />
          <div className="flex-1">
            <div className="font-heading text-sm font-medium">Cambia password</div>
            <div className="text-xs text-white/50">Aggiorna la password del tuo account</div>
          </div>
        </button>

        {showPw && (
          <div className="card-surface p-4 space-y-3 animate-pop">
            <input
              data-testid="new-password-input"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="input-gold"
              placeholder="Nuova password"
            />
            <button
              data-testid="save-password-btn"
              onClick={changePw}
              disabled={saving || !newPw}
              className="btn-gold w-full py-3"
            >
              {saving ? 'Salvataggio…' : 'Salva'}
            </button>
          </div>
        )}

        <button
          data-testid="push-toggle-btn"
          onClick={togglePush}
          disabled={pushBusy}
          className="card-surface p-4 w-full flex items-center gap-4 text-left"
        >
          {pushActive ? (
            <Bell className="w-5 h-5 text-[var(--gold)]" />
          ) : (
            <BellOff className="w-5 h-5 text-white/50" />
          )}
          <div className="flex-1">
            <div className="font-heading text-sm font-medium flex items-center gap-2">
              Notifiche push
              {pushBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/50" />}
            </div>
            <div className="text-xs text-white/50">
              {!pushState.supported
                ? 'Non supportate su questo browser'
                : pushBlocked
                  ? 'Bloccate dal sistema — sblocca dai permessi del browser'
                  : pushActive
                    ? 'Attive · ricevi avvisi per messaggi, novità e comunicazioni'
                    : 'Disattivate · tocca per attivarle'}
            </div>
          </div>
          <span
            data-testid="push-toggle-switch"
            className={`w-11 h-6 rounded-full relative transition-colors ${pushActive ? 'bg-[var(--gold)]' : 'bg-white/15'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushActive ? 'translate-x-5' : ''}`} />
          </span>
        </button>

        {user?.is_admin && (
          <button
            data-testid="push-test-btn"
            onClick={sendTestPush}
            disabled={testBusy}
            className="card-surface p-4 w-full flex items-center gap-4 text-left disabled:opacity-60"
          >
            <div className="w-9 h-9 rounded-full bg-[var(--gold-soft)] border border-[var(--gold)]/30 flex items-center justify-center">
              {testBusy ? <Loader2 className="w-4 h-4 animate-spin text-[var(--gold)]" /> : <Zap className="w-4 h-4 text-[var(--gold)]" />}
            </div>
            <div className="flex-1">
              <div className="font-heading text-sm font-medium flex items-center gap-2">
                Invia notifica di prova
                <span className="text-[9px] bg-[var(--gold)] text-black px-1.5 py-0.5 rounded-full font-bold tracking-widest">ADMIN</span>
              </div>
              <div className="text-xs text-white/50">
                {testBusy ? 'Invio in corso…' : 'Verifica in un istante che le push arrivino sul tuo dispositivo'}
              </div>
            </div>
          </button>
        )}

        <button
          data-testid="settings-logout-btn"
          onClick={logout}
          className="card-surface p-4 w-full flex items-center gap-4 text-left text-red-400/90"
        >
          <LogOut className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-heading text-sm font-medium">Esci</div>
            <div className="text-xs text-white/40">Termina la sessione</div>
          </div>
        </button>
      </div>
    </Layout>
  );
}
